export type RunPhase = 'title' | 'preflight' | 'active' | 'transition' | 'paused' | 'failed' | 'finale' | 'results';
export type Rank = 'DUST READER' | 'THREADKEEPER' | 'WAYFINDER' | 'MASTER WEAVER';

export interface MovementScore { gates: number; collected: number; missed: number; collisions: number; score: number; }
export interface Point3 { x: number; y: number; z: number; }
export interface RunState {
  readonly seed: string;
  phase: RunPhase;
  movement: number;
  gate: number;
  totalGates: number;
  score: number;
  combo: number;
  maxCombo: number;
  energy: number;
  collisions: number;
  resolvedGates: number;
  collectedGates: number;
  depletedFor: number;
  collisionCooldown: number;
  anchors: Point3[];
  breakdown: MovementScore[];
}

export type GameEvent =
  | { type: 'gate-collected'; gate: number; points: number; combo: number }
  | { type: 'gate-missed'; gate: number }
  | { type: 'collision'; damage: number }
  | { type: 'movement-complete'; movement: number }
  | { type: 'run-failed' }
  | { type: 'run-complete'; score: number; rank: Rank };

export const RULES = { maxEnergy: 100, boostDrain: 13, passiveDrain: 1.12, gateRecovery: 8, missPenalty: 9, collisionDamage: 17, collisionCooldown: 1.15, depletionGrace: 1.35, baseGateScore: 700 } as const;

export function createRun(seed: string, gatesPerMovement: readonly number[]): RunState {
  if (gatesPerMovement.length !== 3 || gatesPerMovement.some((count) => count <= 0)) throw new Error('A run requires three non-empty movements.');
  return {
    seed, phase: 'preflight', movement: 0, gate: 0, totalGates: gatesPerMovement[0] ?? 0, score: 0, combo: 0, maxCombo: 0,
    energy: RULES.maxEnergy, collisions: 0, resolvedGates: 0, collectedGates: 0, depletedFor: 0, collisionCooldown: 0,
    anchors: [], breakdown: gatesPerMovement.map((gates) => ({ gates, collected: 0, missed: 0, collisions: 0, score: 0 })),
  };
}

export function tickRun(state: RunState, delta: number, boosting: boolean): GameEvent[] {
  if (state.phase !== 'active' && state.phase !== 'transition') return [];
  state.collisionCooldown = Math.max(0, state.collisionCooldown - delta);
  const drain = RULES.passiveDrain + (boosting && state.energy > 0 ? RULES.boostDrain : 0);
  state.energy = clamp(state.energy - drain * delta, 0, RULES.maxEnergy);
  state.depletedFor = state.energy <= 0 ? state.depletedFor + delta : 0;
  if (state.depletedFor >= RULES.depletionGrace) { state.phase = 'failed'; return [{ type: 'run-failed' }]; }
  return [];
}

export function resolveGate(state: RunState, hit: boolean, boosted: boolean, anchor?: Point3): GameEvent {
  if (state.phase !== 'active' && state.phase !== 'transition') throw new Error('Gates can only resolve during flight.');
  const index = state.gate; const movement = state.breakdown[state.movement];
  if (!movement) throw new Error('Movement score is unavailable.');
  state.resolvedGates += 1;
  if (hit) {
    state.combo += 1; state.maxCombo = Math.max(state.maxCombo, state.combo); state.collectedGates += 1;
    const multiplier = 1 + Math.min(1.75, state.combo * 0.125) + (boosted ? 0.55 : 0);
    const points = Math.round(RULES.baseGateScore * multiplier);
    state.score += points; state.energy = clamp(state.energy + RULES.gateRecovery, 0, RULES.maxEnergy);
    movement.collected += 1; movement.score += points;
    if (anchor) state.anchors.push({ ...anchor });
    state.gate += 1;
    return { type: 'gate-collected', gate: index, points, combo: state.combo };
  }
  state.combo = 0; state.energy = clamp(state.energy - RULES.missPenalty, 0, RULES.maxEnergy); movement.missed += 1; state.gate += 1;
  return { type: 'gate-missed', gate: index };
}

export function collide(state: RunState): Extract<GameEvent, { type: 'collision' }> | null {
  if (state.phase !== 'active' || state.collisionCooldown > 0) return null;
  state.energy = clamp(state.energy - RULES.collisionDamage, 0, RULES.maxEnergy); state.combo = 0; state.collisions += 1; state.collisionCooldown = RULES.collisionCooldown;
  const movement = state.breakdown[state.movement]; if (movement) movement.collisions += 1;
  return { type: 'collision', damage: RULES.collisionDamage };
}

export function advanceMovement(state: RunState, gatesPerMovement: readonly number[]): GameEvent {
  if (state.gate < state.totalGates) throw new Error('Cannot advance before all gates resolve.');
  if (state.movement >= 2) { const final = finalizeScore(state); state.score = final.score; state.phase = 'finale'; return { type: 'run-complete', score: final.score, rank: final.rank }; }
  state.movement += 1; state.gate = 0; state.totalGates = gatesPerMovement[state.movement] ?? 0; state.phase = 'transition';
  return { type: 'movement-complete', movement: state.movement };
}

export function finalizeScore(state: RunState): { score: number; rank: Rank; accuracy: number } {
  const accuracy = state.resolvedGates ? state.collectedGates / state.resolvedGates : 0;
  const frozen = state.phase === 'finale' || state.phase === 'results';
  const score = frozen ? state.score : Math.max(0, Math.round(state.score + accuracy * 3100 + state.energy * 24 + state.maxCombo * 90 - state.collisions * 260));
  const rank: Rank = score >= 26500 && accuracy >= .88 ? 'MASTER WEAVER' : score >= 19000 && accuracy >= .72 ? 'WAYFINDER' : score >= 11000 ? 'THREADKEEPER' : 'DUST READER';
  return { score, rank, accuracy };
}

export function setPaused(state: RunState, paused: boolean): void {
  if (paused && (state.phase === 'active' || state.phase === 'transition')) state.phase = 'paused'; else if (!paused && state.phase === 'paused') state.phase = 'active';
}

function clamp(value: number, min: number, max: number): number { return Math.min(max, Math.max(min, value)); }
